import { BlockedReasonSchema } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

export const AdaptiveScanningPhaseSchema = z.enum([
  'discovering',
  'attack_ready',
  'attacking',
  'resume_verification',
  'paused_blocked',
]);

export const AdaptiveScanningAggressivenessSchema = z.enum([
  'stealth',
  'balanced',
  'aggressive',
]);

export const AdaptiveScanningResumeModeSchema = z.enum([
  'cautious',
  'continue',
]);

export const AdaptiveScanningTargetBlockReasonSchema = z.enum([
  'waf',
  'rate_limit',
  'anti_bot_system',
  'server_unreachable',
]);

export const AdaptiveScanningWaveTerminalOutcomeSchema = z.enum([
  'success',
  'blocked',
  'auth',
  'our_error',
  'running',
]);

export const AdaptiveScanningWaveTerminalSchema = z.object({
  signalId: z.string(),
  outcome: AdaptiveScanningWaveTerminalOutcomeSchema,
  blockedReason: BlockedReasonSchema.nullable(),
});

export const ADAPTIVE_SCANNING_MAX_WAVE_TERMINALS = 200;

export const AdaptiveScanningLastWaveSchema = z.object({
  signalIds: z.array(z.string()),
  startedAt: z.string(),
  terminals: z
    .array(AdaptiveScanningWaveTerminalSchema)
    .max(ADAPTIVE_SCANNING_MAX_WAVE_TERMINALS),
});

export const AdaptiveScanningBlockWindowSchema = z.object({
  windowStartedAt: z.string(),
  counts: z.partialRecord(BlockedReasonSchema, z.number().nonnegative()),
  terminals: z.number().nonnegative(),
  targetBlocks: z.number().nonnegative(),
  outOfBandBlocks: z.number().nonnegative().default(0),
});

const blockWindowMinutes = z.number().int().min(5).max(1440);
const waveTimeoutMinutes = z.number().int().min(5).max(1440);
const blockCountThreshold = z.number().int().min(1).max(25);
const blockRateThreshold = z.number().min(0.05).max(1);
const minSamples = z.number().int().min(1).max(25);
const attackTiersEnabled = z.number().int().min(0).max(4);

export const AdaptiveScanningPolicyConfigSchema = z.object({
  aggressiveness: AdaptiveScanningAggressivenessSchema.optional(),
  attackTiersEnabled: attackTiersEnabled.optional(),
  originPolicy: z.literal('source_code_first').optional(),
  blockWindowMinutes: blockWindowMinutes.optional(),
  waveTimeoutMinutes: waveTimeoutMinutes.optional(),
  blockCountThreshold: blockCountThreshold.optional(),
  blockRateThreshold: blockRateThreshold.optional(),
  minSamples: minSamples.optional(),
  autoResumeOnRateLimit: z.boolean().optional(),
  notifySlack: z.boolean().optional(),
  notifyInApp: z.boolean().optional(),
});

export const AdaptiveScanningResolvedPolicyConfigSchema = z.object({
  aggressiveness: AdaptiveScanningAggressivenessSchema,
  attackTiersEnabled,
  originPolicy: z.literal('source_code_first'),
  blockWindowMinutes,
  waveTimeoutMinutes,
  blockCountThreshold,
  blockRateThreshold,
  minSamples,
  autoResumeOnRateLimit: z.boolean(),
  notifySlack: z.boolean(),
  notifyInApp: z.boolean(),
});

export const AdaptiveScanningBlockedReasonCountSchema = z.object({
  reason: BlockedReasonSchema,
  count: z.number().int().nonnegative(),
});

export const AdaptiveScanningStateSchema = z.object({
  applicationId: z.string(),
  enabled: z.boolean(),
  phase: AdaptiveScanningPhaseSchema.nullable(),
  iteration: z.number().int().nonnegative(),
  aggressiveness: AdaptiveScanningAggressivenessSchema,
  unlockedTier: z.number().int().min(0).max(4),
  waveSize: z.number().int().positive(),
  pausedReason: BlockedReasonSchema.nullable(),
  pausedUntil: z.string().nullable(),
  autoResumeAt: z.string().nullable(),
  lastPlannedAt: z.string().nullable(),
  lastWave: AdaptiveScanningLastWaveSchema.nullable(),
  blockWindow: AdaptiveScanningBlockWindowSchema.nullable(),
  rerunBlockedReasons: z.array(BlockedReasonSchema),
  verifyWave: z.number().int().nullable(),
  verifyTargetIds: z.array(z.string()),
  blockedReasonCounts: z.array(AdaptiveScanningBlockedReasonCountSchema),
  config: AdaptiveScanningResolvedPolicyConfigSchema,
});

export const AdaptiveScanningResumeBodySchema = z.object({
  mode: AdaptiveScanningResumeModeSchema.default('cautious'),
  rerunBlockedReasons: z
    .array(AdaptiveScanningTargetBlockReasonSchema)
    .min(1)
    .optional(),
  skipVerification: z.boolean().default(false),
});

export const AdaptiveScanningPauseBodySchema = z.object({
  reason: AdaptiveScanningTargetBlockReasonSchema.optional(),
});

export const AdaptiveScanningEnabledBodySchema = z.object({
  enabled: z.boolean(),
});

export const AdaptiveScanningWaveResultSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  triggeredCount: z.number().int().nonnegative(),
  scheduledSignals: z.array(z.string()),
  phase: AdaptiveScanningPhaseSchema.nullable(),
  verifyWave: z.number().int().nullable(),
});

export type AdaptiveScanningPhase = z.infer<typeof AdaptiveScanningPhaseSchema>;
export type AdaptiveScanningAggressiveness = z.infer<
  typeof AdaptiveScanningAggressivenessSchema
>;
export type AdaptiveScanningResumeMode = z.infer<
  typeof AdaptiveScanningResumeModeSchema
>;
export type AdaptiveScanningTargetBlockReason = z.infer<
  typeof AdaptiveScanningTargetBlockReasonSchema
>;
export type AdaptiveScanningStateDto = z.infer<
  typeof AdaptiveScanningStateSchema
>;
export type AdaptiveScanningResumeBody = z.infer<
  typeof AdaptiveScanningResumeBodySchema
>;
export type AdaptiveScanningPauseBody = z.infer<
  typeof AdaptiveScanningPauseBodySchema
>;
export type AdaptiveScanningEnabledBody = z.infer<
  typeof AdaptiveScanningEnabledBodySchema
>;
export type AdaptiveScanningWaveResult = z.infer<
  typeof AdaptiveScanningWaveResultSchema
>;
export type AdaptiveScanningPolicyConfig = z.infer<
  typeof AdaptiveScanningPolicyConfigSchema
>;
export type AdaptiveScanningResolvedPolicyConfig = z.infer<
  typeof AdaptiveScanningResolvedPolicyConfigSchema
>;
